import getSuffixForAcabado from '@context/utils/linked/getSuffixForAcabado'
import {
  buildLateralCostadoTextureFromPuerta,
  pickZoneCatalogFields,
  replaceZoneMaterialFromTexture,
} from '@utils/sceneObjects/linked/linkedTextureFields'
import {
  isEncimeraLinkedSonItem,
  isIndependentZocaloLinkedSon,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import { AnyID, LinkedItem, Textures } from '@types'

function isZocaloTextureZone(texture: {
  zoneDisplayName?: string | null
  zoneName?: string | null
  zone?: string | null
}): boolean {
  return (
    /z[oó]calo/i.test(String(texture.zoneDisplayName ?? '')) ||
    /z[oó]calo/i.test(String(texture.zoneName ?? '')) ||
    /z[oó]calo/i.test(String(texture.zone ?? ''))
  )
}

function isEncimeraTextureZone(texture: {
  zoneDisplayName?: string | null
  zoneName?: string | null
  zone?: string | null
  acabado?: string | null
}): boolean {
  return (
    /encimera/i.test(String(texture.zoneDisplayName ?? '')) ||
    /encimera/i.test(String(texture.zone ?? '')) ||
    /encimera/i.test(String(texture.acabado ?? ''))
  )
}

/** Compacto de Encimera b30 usa repeat 1; el GLB BR40100 trae 0.001 (PBR de catálogo vacío). */
function pickPreferredEncimeraTexture(textures: any[]): any | undefined {
  const encimeras = textures.filter(isEncimeraTextureZone)
  if (!encimeras.length) return undefined
  return (
    encimeras.find((texture) => Number(texture?.repeatX) >= 0.01) ?? encimeras[0]
  )
}

/** Normaliza texturas del hijo Zocalo 4000 a zoneDisplayName "Zocalo" para mapear al dibujar. */
function normalizeZocaloSonTexturesForInherit(textures: any[]): any[] {
  return textures.map((t) => {
    if (!t) return t
    if (isZocaloTextureZone(t)) {
      return { ...t, zoneDisplayName: 'Zocalo', zoneName: t.zoneName || 'Zocalo', zone: t.zone || 'Zocalo' }
    }
    // Hijo independiente: a menudo la zona GLB no se llama Zocalo (p. ej. Box001).
    return {
      ...t,
      zoneDisplayName: 'Zocalo',
      zoneName: t.zoneName || 'Zocalo',
      zone: t.zone || 'Zocalo',
    }
  })
}

// export function getExistingTexturesFromScene(items: Map<string, any>): Textures | null {
//   const linkedItems = Array.from(items.values()).filter(
//     (item): item is LinkedItem =>
//       item.type === 'linkedItem' && Array.isArray(item.textures) && item.textures.length > 0,
//   )

//   if (linkedItems.length === 0) {
//     return null
//   }

// const nonEncimeraItems = linkedItems.filter((item) => {
//   const hasEncimeraTexture = item.textures.some(
//     (texture: any) =>
//       texture.zone === 'Encimera' ||
//       texture.zoneDisplayName === 'Encimera' ||
//       texture.acabado?.toLowerCase().includes('encimera'),
//   )
//   return !hasEncimeraTexture
// })

// const targetItem = nonEncimeraItems.length > 0 ? nonEncimeraItems[0] : linkedItems[0]

//   return targetItem.textures
// }

function isKABVCatalog(catalogID?: string | number | null): boolean {
  if (catalogID == null) return false
  const upperCatalogID = String(catalogID).toUpperCase()
  return (
    upperCatalogID.startsWith('KABV') ||
    upperCatalogID.startsWith('KAPV') ||
    upperCatalogID.startsWith('PE KABV') ||
    upperCatalogID.startsWith('PE KAPV')
  )
}

const collectedTextureKey = (texture: any): string =>
  (texture.zoneName ?? texture.zoneDisplayName ?? '')?.toString().toUpperCase().trim() ?? ''

type TextureEntry = {
  zoneDisplayName: string
  name: string
  acabado: string
  id: number
  searchID: number
  url: string
  groupID: number
  zoneName: string
  modelo: string
}

const isTiradorTexture = (texture: any): boolean => {
  const zoneDisplay = texture.zoneDisplayName?.toLowerCase()
  const zoneName = texture.zoneName?.toLowerCase()
  return (
    (zoneDisplay && zoneDisplay.includes('tirador')) ||
    (zoneName && zoneName.includes('tirador')) ||
    zoneDisplay === 'tirador (44)' ||
    zoneName === 'edge_straight_200mm'
  )
}

const makeTextureKey = (texture: any): string =>
  (texture.zoneName ?? texture.zoneDisplayName ?? '')?.toString().toUpperCase().trim() ?? ''

function enrichCollectedTexturesFromItemZones(item: LinkedItem, collected: Map<string, any>) {
  if (!item.textures?.length || !item.zones) return

  for (const tex of item.textures) {
    const key = makeTextureKey(tex)
    const entry = collected.get(key)
    if (!entry) continue

    let zoneKey: string | null =
      tex.zoneName && item.zones[tex.zoneName] ? tex.zoneName : null
    if (!zoneKey) {
      zoneKey =
        Object.keys(item.zones).find(
          (k) =>
            item.zones[k]?.material?.zoneDisplayName === tex.zoneDisplayName ||
            item.zones[k]?.name === tex.zoneName,
        ) ?? null
    }

    const mat = zoneKey ? item.zones[zoneKey]?.material : undefined
    if (!mat) continue
    Object.assign(entry, pickZoneCatalogFields(mat))
  }
}

function collectTextures(target: Map<string, any>, textures: any[]) {
  textures.forEach((texture: any) => {
    if (!texture) return

    const zoneDisplayName = String(texture.zoneDisplayName || '').toLowerCase()
    const zoneName = String(texture.zoneName || '').toLowerCase()
    const zone = String(texture.zone || '').toLowerCase()

    if (zoneDisplayName === 'rejilla' || zoneName === 'rejilla' || zone === 'rejilla') {
      return
    }

    const key = makeTextureKey(texture)
    if (!key || target.has(key)) return
    target.set(key, { ...texture })
  })
}

export function getExistingTexturesFromScene(
  items: Map<string, any>,
  targetCatalogID?: string | number,
  currentSourceID?: AnyID,
): any[] {
  const collected = new Map<string, any>()
  const targetIsKABV = isKABVCatalog(targetCatalogID)

  const applyItem = (item: LinkedItem, includeAll: boolean) => {
    if (!includeAll) {
      collectTextures(collected, item.textures.filter(isTiradorTexture))
    } else {
      collectTextures(collected, item.textures)
    }
    enrichCollectedTexturesFromItemZones(item, collected)
  }

  const currentItem = currentSourceID
    ? (items.get(currentSourceID as AnyID) as LinkedItem)
    : undefined

  if (
    currentItem &&
    (currentItem.type === 'linkedItem' || currentItem.type === 'linkedSon') &&
    Array.isArray(currentItem.textures) &&
    currentItem.textures.length > 0
  ) {
    const sourceIsKABV = isKABVCatalog(currentItem.catalogID)
    const includeAll = (!sourceIsKABV && !targetIsKABV) || (sourceIsKABV && targetIsKABV)
    applyItem(currentItem, includeAll)
  }

  // If the selected item is a regular mueble (not an encimera itself), also collect
  // textures from its encimera linkedSon child so they can be inherited by a standalone encimera.
  if (
    currentItem &&
    currentItem.type === 'linkedItem' &&
    !/encimera/i.test((currentItem as any).description ?? '')
  ) {
    for (const candidate of items.values()) {
      if (
        candidate.type === 'linkedSon' &&
        (candidate as any).firstParentID === (currentItem as any).id &&
        /encimera/i.test(candidate.description ?? (candidate as any).typeOfSon ?? '') &&
        Array.isArray(candidate.textures) &&
        candidate.textures.length > 0
      ) {
        collectTextures(collected, candidate.textures)
        break
      }
    }
  }

  // Zocalo 4000 vive en el linkedSon, no en textures[] del padre: recogerlo para heredar al dibujar.
  if (currentItem && currentItem.type === 'linkedItem') {
    let zocaloSon =
      (currentItem as any).independentZocaloSonId != null
        ? items.get((currentItem as any).independentZocaloSonId as AnyID)
        : undefined
    if (!zocaloSon) {
      for (const candidate of items.values()) {
        if (
          candidate.type === 'linkedSon' &&
          (candidate as any).firstParentID === (currentItem as any).id &&
          isIndependentZocaloLinkedSon(candidate) &&
          Array.isArray(candidate.textures) &&
          candidate.textures.length > 0
        ) {
          zocaloSon = candidate
          break
        }
      }
    }
    if (zocaloSon && Array.isArray(zocaloSon.textures) && zocaloSon.textures.length > 0) {
      collectTextures(collected, normalizeZocaloSonTexturesForInherit(zocaloSon.textures))
    }
  }

  // Encimera vive en el linkedSon (zona GLB Box001 vs Zocalo004). Recoger todas
  // para que BR40100 herede Compacto de Encimera b30, no el PBR vacío del catálogo.
  for (const candidate of items.values()) {
    if (candidate.type !== 'linkedSon') continue
    if (!isEncimeraLinkedSonItem(candidate)) continue
    if (!Array.isArray(candidate.textures) || candidate.textures.length === 0) continue
    if (candidate === currentItem) continue
    collectTextures(collected, candidate.textures)
  }

  for (const item of items.values()) {
    if (
      item.type !== 'linkedItem' ||
      !Array.isArray(item.textures) ||
      item.textures.length === 0 ||
      item === currentItem
    )
      continue

    // Skip standalone encimeras (linkedItems whose description is "Encimera").
    // Their "Encimera" zone texture should only be inherited from the explicitly
    // selected currentItem, not picked up randomly from the scene.
    if (/encimera/i.test(item.description ?? '')) continue

    const itemIsKABV = isKABVCatalog(item.catalogID)
    const includeAll = (!itemIsKABV && !targetIsKABV) || (itemIsKABV && targetIsKABV)
    applyItem(item, includeAll)
  }

  const textures = Array.from(collected.values())

  return textures
}

/**
 * Busca texturas de "Puerta" que tengan acabados especiales (Lara, Lineal, Rustik, etc.)
 *
 * IMPORTANTE: Esta función SOLO se aplica a linkedItems, NO a universales
 */
export function getSuffixFromExistingTextures(items: Map<string, any>, catalogID: string): string {
  const noSuffixButInheritTextures = [
    'DECO',
    'KABV', // Hereda vitrina sólo si currentObjectID es una vitrina
    'CC',
    'CB',
    'FA',
    'LC',
    'LA',
    'LB',
    'RC',
    'RA',
    'RB',
  ]

  const hasExistingSuffix = catalogID.match(/ (LA|LI|Ru|R2)$/)
  if (hasExistingSuffix) {
    return ''
  }

  for (const prefix of noSuffixButInheritTextures) {
    if (prefix === 'DECO' || prefix === 'KABV') {
      if (catalogID.toUpperCase().includes(prefix)) {
        return ''
      }
    } else {
      if (catalogID.toUpperCase().startsWith(prefix)) {
        return ''
      }
    }
  }

  for (const item of items.values()) {
    if (item.type === 'linkedItem' && item.textures && Array.isArray(item.textures)) {
      const puertaTexture = item.textures.find(
        (texture: any) =>
          texture.zoneDisplayName === 'Puerta' &&
          texture.acabado &&
          ['LARA', 'LINEAL', 'RUSTIK', 'RUSTIK2'].some((acabado) =>
            texture.acabado.toUpperCase().includes(acabado),
          ),
      )

      if (puertaTexture) {
        const suffix = getSuffixForAcabado(puertaTexture.acabado)

        return suffix
      }
    }
  }

  return ''
}

export function getTexturesByCatalogID(
  items: Map<string, any>,
  catalogID: string,
): Textures | null {
  const item = Array.from(items.values()).find(
    (item): item is LinkedItem =>
      item.type === 'linkedItem' &&
      item.catalogID === catalogID &&
      Array.isArray(item.textures) &&
      item.textures.length > 0,
  )

  return item ? item.textures : null
}

export function mapExistingTexturesToNewItem(
  existingTextures: Textures,
  newItemZones: string[],
): Partial<Record<string, any>> {
  const textureMap: Partial<Record<string, any>> = {}

  existingTextures.forEach((texture: any) => {
    const matchingZone = newItemZones.find(
      (zone) =>
        zone === texture.zone ||
        zone === texture.zoneDisplayName ||
        texture.zoneDisplayName?.toLowerCase().includes(zone.toLowerCase()) ||
        zone.toLowerCase().includes(texture.zoneDisplayName?.toLowerCase() || ''),
    )

    if (matchingZone) {
      textureMap[matchingZone] = {
        id: texture.id,
        searchID: texture.searchID,
        zoneName: texture.zoneName,
        zone: texture.zone,
        name: texture.name,
        url: texture.url,
        zoneDisplayName: texture.zoneDisplayName,
        groupID: texture.groupID,
        linkedTextureID: texture.linkedTextureID,
        modelo: texture.modelo,
        acabado: texture.acabado,
        idAcabadoUnidadModeloAcabado: texture.idAcabadoUnidadModeloAcabado,
      }
    }
  })

  return textureMap
}

const INHERITED_TEXTURE_KEYS = [
  'name',
  'acabado',
  'id',
  'searchID',
  'url',
  'groupID',
  'envMapIntensity',
  'metalness',
  'roughness',
  'opacity',
  'color',
  'bumpMap',
  'bumpScale',
  'displacement',
  'displacementScale',
  'mirror',
  'repeatX',
  'repeatY',
  'textureRotation',
] as const

function mergeInheritedTextureFields(newTexture: any, sourceTexture: any): any {
  const merged = { ...newTexture }
  for (const key of INHERITED_TEXTURE_KEYS) {
    if (sourceTexture[key] !== undefined) merged[key] = sourceTexture[key]
  }
  return merged
}

export function mapExistingTexturesToNewItemByDisplayName(
  existingTextures: any[],
  newItemTextures: any[],
): any[] {
  return newItemTextures.map((newTexture) => {
    const existingPuertaTexture = existingTextures.find(
      (existing) => existing.zoneDisplayName === 'Puerta',
    )

    if (newTexture.zoneDisplayName === 'Costado' && existingPuertaTexture) {
      return buildLateralCostadoTextureFromPuerta(newTexture, existingPuertaTexture)
    }

    if (isEncimeraTextureZone(newTexture)) {
      const preferredEncimera = pickPreferredEncimeraTexture(existingTextures)
      if (preferredEncimera) {
        return mergeInheritedTextureFields(newTexture, preferredEncimera)
      }
    }

    let matchingTexture = existingTextures.find(
      (existing) => existing.zoneDisplayName === newTexture.zoneDisplayName,
    )

    // Zocalo 4000: zona GLB puede no llamarse "Zocalo"; casar por nombre de zona suelto.
    if (!matchingTexture && isZocaloTextureZone(newTexture)) {
      matchingTexture = existingTextures.find(
        (existing) =>
          existing.zoneDisplayName === 'Zocalo' || isZocaloTextureZone(existing),
      )
    }

    if (matchingTexture) {
      return mergeInheritedTextureFields(newTexture, matchingTexture)
    }

    return newTexture
  })
}

export function mapExistingTexturesToNewItemByDisplayNameWithDecoLogic(
  existingTextures: Textures,
  newItemTextures: Textures,
  newItemCatalogID: string,
): Textures {
  return newItemTextures.map((newTexture: any) => {
    const isNewItemDeco =
      (typeof newItemCatalogID === 'string' && newItemCatalogID.includes('DECO')) ||
      (newItemCatalogID as any)?.referencia?.includes('DECO')

    let matchingExistingTexture = null

    if (
      isNewItemDeco &&
      newTexture.zoneDisplayName === 'Armazon' &&
      newTexture.modelo === 'AcabadoModelo'
    ) {
      matchingExistingTexture = existingTextures.find(
        (existingTexture: any) =>
          existingTexture.zoneDisplayName === 'Puerta' &&
          existingTexture.modelo === 'AcabadoModelo',
      )
    } else if (
      !isNewItemDeco &&
      newTexture.zoneDisplayName === 'Puerta' &&
      newTexture.modelo === 'AcabadoModelo'
    ) {
      matchingExistingTexture = existingTextures.find(
        (existingTexture: any) =>
          existingTexture.zoneDisplayName === 'Armazon' &&
          existingTexture.modelo === 'AcabadoModelo',
      )
    } else {
      if (!isNewItemDeco && newTexture.zoneDisplayName === 'Armazon') {
        matchingExistingTexture = null
      } else {
        matchingExistingTexture = existingTextures.find(
          (existingTexture: any) =>
            existingTexture.zoneDisplayName === newTexture.zoneDisplayName ||
            existingTexture.zone === newTexture.zoneDisplayName ||
            newTexture.zoneDisplayName === existingTexture.zone,
        )
      }
    }

    if (matchingExistingTexture) {
      const isEncimera =
        newTexture.zoneDisplayName === 'Encimera' ||
        newTexture.zone === 'Encimera' ||
        newTexture.acabado?.toLowerCase().includes('encimera')

      const isDecoArmazonMapping =
        (isNewItemDeco &&
          newTexture.zoneDisplayName === 'Armazon' &&
          newTexture.modelo === 'AcabadoModelo') ||
        (!isNewItemDeco &&
          newTexture.zoneDisplayName === 'Puerta' &&
          newTexture.modelo === 'AcabadoModelo')

      return {
        ...newTexture,
        id: matchingExistingTexture.id,
        searchID: isEncimera ? newTexture.searchID : matchingExistingTexture.searchID,
        name: matchingExistingTexture.name,
        url: matchingExistingTexture.url,
        groupID: matchingExistingTexture.groupID,
        linkedTextureID: isEncimera
          ? matchingExistingTexture.linkedTextureID && matchingExistingTexture.linkedTextureID > 0
            ? matchingExistingTexture.linkedTextureID
            : newTexture.linkedTextureID
          : isDecoArmazonMapping
            ? newTexture.linkedTextureID
            : matchingExistingTexture.linkedTextureID,
        modelo: matchingExistingTexture.modelo,
        acabado: matchingExistingTexture.acabado,
        idAcabadoUnidadModeloAcabado: isDecoArmazonMapping
          ? newTexture.idAcabadoUnidadModeloAcabado
          : matchingExistingTexture.idAcabadoUnidadModeloAcabado,
      }
    }

    return newTexture
  })
}

export function syncZonesWithMappedTextures(
  zones: any,
  mappedTextures: Textures,
  existingItems?: Map<string, any>,
  itemType?: string,
  itemDescription?: string,
): { zones: any; inheritedHeight?: number; inheritedMorphs?: any[]; inheritedTexture?: any } {
  const updatedZones = { ...zones }
  let inheritedHeight: number | undefined = undefined
  let inheritedMorphs: any[] | undefined = undefined
  let inheritedTexture: any = undefined

  for (const texture of mappedTextures) {
    let zoneName = texture.zoneName
    if (!updatedZones[zoneName]) {
      const fallbackKey = Object.keys(updatedZones).find((key) => {
        const mat = updatedZones[key]?.material
        return (
          mat?.zoneDisplayName === texture.zoneDisplayName ||
          mat?.zone === texture.zoneDisplayName ||
          mat?.zone === texture.zone
        )
      })
      if (fallbackKey) {
        zoneName = fallbackKey
        texture.zoneName = fallbackKey
      }
    }
    if (!updatedZones[zoneName]) continue

    const isEncimera = itemType === 'linkedSon' && itemDescription === 'Encimera'
    const isInheritedCostado = texture.zoneDisplayName === 'Costado' && !isEncimera

    if (isInheritedCostado) {
      updatedZones[zoneName] = {
        ...updatedZones[zoneName],
        material: replaceZoneMaterialFromTexture(
          texture,
          updatedZones[zoneName]?.material ?? {},
        ),
      }
      continue
    }

    let existingMaterial: any = null
    let existingEncimeraItem: any = null

    if (existingItems) {
      for (const item of existingItems.values()) {
        if (item.type !== 'linkedItem' || !item.zones) continue

        if (isEncimera && item.childs) {
          for (const childId of item.childs) {
            const childItem = existingItems.get(childId)
            if (
              childItem &&
              childItem.type === 'linkedSon' &&
              childItem.description === 'Encimera'
            ) {
              if (childItem.textures && childItem.textures.length > 0) {
                const encimeraTexture = childItem.textures.find((t: any) => t.zone === 'Encimera')
                if (encimeraTexture) {
                  for (const [, childZone] of Object.entries(childItem.zones || {})) {
                    const mat = (childZone as any)?.material
                    if (
                      mat &&
                      mat.acabado === encimeraTexture.acabado &&
                      mat.name === encimeraTexture.name &&
                      mat.url === encimeraTexture.url
                    ) {
                      existingMaterial = mat
                      existingEncimeraItem = childItem
                      break
                    }
                  }
                }
              }
              if (existingMaterial) break
            }
          }
        } else {
          for (const [, existingZone] of Object.entries(item.zones as Record<string, any>)) {
            const mat = (existingZone as any)?.material
            if (mat && mat.id === texture.id && mat.searchID === texture.searchID) {
              const nameMatches = mat.name === texture.name
              const urlMatches = mat.url === texture.url
              // const acabadoMatches = mat.acabado === texture.acabado

              if (nameMatches && urlMatches) {
                existingMaterial = mat
                break
              }
            }
          }
        }
        if (existingMaterial) break
      }
    }

    if (existingMaterial) {
      if (isEncimera && existingEncimeraItem) {
        const hybridMaterial = {
          ...existingMaterial,
          id: texture.id,
          searchID: texture.searchID,
          linkedTextureID: texture.linkedTextureID,
          name: existingMaterial.name,
          url: existingMaterial.url,
          description: existingMaterial.description,
          acabado: existingMaterial.acabado,
        }

        updatedZones[zoneName] = {
          ...updatedZones[zoneName],
          material: hybridMaterial,
        }

        if (existingEncimeraItem.height) {
          inheritedHeight = existingEncimeraItem.height
        }

        if (existingEncimeraItem.morphs && existingEncimeraItem.morphs.length > 0) {
          const alturaMorph = existingEncimeraItem.morphs.find((m: any) => m.name === 'Alto')
          if (alturaMorph) {
            inheritedMorphs = [alturaMorph]
          }
        }

        inheritedTexture = {
          name: existingMaterial.name,
          url: existingMaterial.url,
          description: existingMaterial.description,
          acabado: existingMaterial.acabado,
          id: existingMaterial.id,
          searchID: existingMaterial.searchID,
        }
      } else {
        updatedZones[zoneName] = {
          ...updatedZones[zoneName],
          material: { ...existingMaterial },
        }
      }
      continue
    }

    if (existingItems && !existingMaterial) {
      for (const item of existingItems.values()) {
        if (item.type !== 'linkedItem' || !item.zones) continue

        for (const [, existingZone] of Object.entries(item.zones as Record<string, any>)) {
          const mat = (existingZone as any)?.material
          if (mat && (mat.name === texture.name || mat.url === texture.url)) {
            existingMaterial = mat
            break
          }
        }
        if (existingMaterial) break
      }
    }

    if (existingMaterial) {
      updatedZones[zoneName] = {
        ...updatedZones[zoneName],
        material: { ...existingMaterial },
      }
      continue
    }

    const currentMaterial = updatedZones[zoneName]?.material ?? {}

    updatedZones[zoneName] = {
      ...updatedZones[zoneName],
      material: replaceZoneMaterialFromTexture(texture, currentMaterial),
    }
  }

  return { zones: updatedZones, inheritedHeight, inheritedMorphs, inheritedTexture }
}
